import React, { useState } from "react"
import ReactApexChart from "react-apexcharts"

import { GET, httpOk } from "helpers/api"
import { formatDate } from "helpers/helpers"

import KpiCard from "../parts/KpiCard"
import MetricHint from "../parts/MetricHint"
import {
	Card, EmptyNote, baseChartOptions, CHART_COLORS,
	convert, formatAmount, formatNumber, formatPercent, mutedStyle,
} from "../parts/common"

/**
 * Бренды и сети.
 *
 * Точка ≠ клиент: 299 точек — это 246 брендов. Деньги и отток живут на бренде,
 * оборот меряется точками. Рост «точек на бренд» — самый дешёвый рост выручки:
 * новая точка у своего клиента не требует ни агента, ни внедрения с нуля.
 */
function Brands({ data, currency, rates, preset }) {
	const summary = data.summary || {}
	const months = data.months || []
	const distribution = data.distribution || []
	const ranking = data.ranking || []

	const [expanded, setExpanded] = useState(null)
	const [points, setPoints] = useState({})

	const lastMonth = months.length > 0 ? months[months.length - 1].month : null
	const amount = (value, month = lastMonth) => formatAmount(convert(value, month, currency, rates), currency)

	// Точки бренда подгружаются по клику на строку, а не вместе со всем рейтингом.
	async function toggleBrand(login) {
		if (expanded === login) {
			setExpanded(null)
			return
		}

		setExpanded(login)
		if (points[login]) return

		const response = await GET("/admin/analytics/brands", { login, preset }, { loader: false })
		if (httpOk(response)) {
			setPoints((prev) => ({ ...prev, [login]: response.data.items || [] }))
		}
	}

	// Сети крупнее пяти точек в отдельную корзину: иначе хвост гистограммы
	// состоит из столбиков высотой в один бренд и ничего не показывает.
	const buckets = [1, 2, 3, 4].map((size) => ({
		label: size === 1 ? "1 точка" : `${size} точки`,
		brands: distribution.find((row) => row.points_in_brand === size)?.brands || 0,
	}))
	buckets.push({
		label: "5 и больше",
		brands: distribution.filter((row) => row.points_in_brand >= 5).reduce((sum, row) => sum + row.brands, 0),
	})

	const distributionChart = {
		options: {
			...baseChartOptions,
			chart: { ...baseChartOptions.chart, type: "bar", height: 280 },
			colors: [CHART_COLORS[0]],
			plotOptions: { bar: { borderRadius: 4, columnWidth: "45%", distributed: false } },
			dataLabels: { enabled: true, style: { fontSize: "11px", colors: ["#111827"] }, offsetY: -18 },
			xaxis: { ...baseChartOptions.xaxis, categories: buckets.map((row) => row.label) },
			legend: { show: false },
		},
		series: [{ name: "Брендов", data: buckets.map((row) => row.brands) }],
	}

	const dynamicsChart = {
		options: {
			...baseChartOptions,
			chart: { ...baseChartOptions.chart, type: "line", height: 300, stacked: false },
			colors: [CHART_COLORS[0], CHART_COLORS[1], CHART_COLORS[3]],
			stroke: { width: [0, 0, 3], curve: "smooth" },
			plotOptions: { bar: { borderRadius: 3, columnWidth: "55%" } },
			xaxis: { ...baseChartOptions.xaxis, categories: months.map((row) => row.label) },
			yaxis: [
				{ labels: { style: { colors: "#8791a1", fontSize: "11px" } } },
				{ show: false },
				{
					opposite: true,
					title: { text: "Точек на бренд", style: { color: "#8791a1", fontSize: "11px" } },
					labels: {
						style: { colors: "#8791a1", fontSize: "11px" },
						formatter: (value) => Number(value).toFixed(2),
					},
				},
			],
		},
		series: [
			{ name: "Новые бренды", type: "column", data: months.map((row) => row.new_brands) },
			{ name: "Расширение сетей", type: "column", data: months.map((row) => row.expansion_points) },
			{ name: "Точек на бренд", type: "line", data: months.map((row) => row.points_per_brand) },
		],
	}

	return (
		<>
			<div className="row g-2 mb-3">
				<div className="col-md-3">
					<KpiCard title="Брендов" hintKey="metric_brand_hint" value={formatNumber(summary.brands)}
						sublabel={`точек: ${formatNumber(summary.points)}`} />
				</div>
				<div className="col-md-3">
					<KpiCard title="Сетевых брендов" hintKey="metric_network_brand_hint"
						value={formatNumber(summary.network_brands)}
						sublabel={`точек в сетях: ${formatNumber(summary.network_points)}`} accent="#1f9d57" />
				</div>
				<div className="col-md-3">
					<KpiCard title="Доля сетей в MRR" hintKey="metric_network_brand_hint"
						value={formatPercent(summary.network_mrr_share)}
						sublabel={`одиночных брендов: ${formatNumber(summary.single_brands)}`} accent="#9b5de5" />
				</div>
				<div className="col-md-3">
					<KpiCard title="Точек на бренд" hintKey="metric_points_per_brand_hint"
						value={Number(summary.points_per_brand || 0).toFixed(2)}
						sublabel={summary.network_premium
							? `сеть платит в ${summary.network_premium} раза больше одиночки`
							: null} accent="#f0a04c" />
				</div>
			</div>

			<div className="row g-2 mb-2">
				<div className="col-md-4">
					<Card title="Размер сети" hint={<MetricHint hintKey="metric_network_brand_hint" />}>
						<ReactApexChart options={distributionChart.options} series={distributionChart.series}
							type="bar" height={280} />
						<div style={mutedStyle}>
							Средний MRR сети — {amount(summary.avg_network_mrr)}, одиночной точки — {amount(summary.avg_single_mrr)}
						</div>
					</Card>
				</div>
				<div className="col-md-8">
					<Card title="Новые бренды и расширение существующих"
						hint={<MetricHint hintKey="metric_expansion_hint" />}>
						<ReactApexChart options={dynamicsChart.options} series={dynamicsChart.series}
							type="line" height={300} />
					</Card>
				</div>
			</div>

			<Card title="Рейтинг брендов" right={<span style={mutedStyle}>нажмите на строку, чтобы увидеть точки</span>}>
				{ranking.length === 0 && <EmptyNote>Нет данных за период</EmptyNote>}

				{ranking.length > 0 && (
					<div className="table-responsive">
						<table className="table table-sm table-hover table-bordered mb-0" style={{ fontSize: "12px" }}>
							<thead>
								<tr>
									<th>Бренд</th>
									<th className="text-end">Точек</th>
									<th className="text-end">MRR</th>
									<th className="text-end">Оборот за период</th>
									<th className="text-end">Чеков</th>
									<th className="text-end">Средний чек</th>
									<th className="text-center">С нами с</th>
									<th>Агент</th>
								</tr>
							</thead>
							<tbody>
								{ranking.map((row) => (
									<React.Fragment key={row.login}>
										<tr style={{ cursor: "pointer" }} onClick={() => toggleBrand(row.login)}>
											<td>
												<i className={`uil-angle-${expanded === row.login ? "down" : "right"}`}></i>
												<b>{row.name}</b>
												<span style={mutedStyle}> · {row.login}</span>
											</td>
											<td className="text-end">{row.points}</td>
											<td className="text-end">{amount(row.mrr)}</td>
											<td className="text-end">{amount(row.gtv)}</td>
											<td className="text-end">{formatNumber(row.cheques)}</td>
											<td className="text-end">{amount(row.avg_cheque)}</td>
											<td className="text-center">{row.created_at ? formatDate(row.created_at, "MM.yyyy") : "—"}</td>
											<td>{row.agent_name || "—"}</td>
										</tr>

										{expanded === row.login && (
											<tr>
												<td colSpan="8" style={{ background: "var(--dark-surface-2, #fafbfc)" }}>
													{!points[row.login] && <div style={mutedStyle}>Загружаем точки…</div>}
													{points[row.login] && points[row.login].length === 0 && (
														<div style={mutedStyle}>У бренда нет точек</div>
													)}
													{points[row.login] && points[row.login].length > 0 && (
														<table className="table table-sm table-bordered mb-0" style={{ fontSize: "12px" }}>
															<thead>
																<tr>
																	<th>Точка</th>
																	<th>Регион</th>
																	<th className="text-end">Тариф</th>
																	<th className="text-end">Баланс</th>
																	<th className="text-end">Оборот</th>
																	<th className="text-end">Чеков</th>
																	<th className="text-center">Последний чек</th>
																</tr>
															</thead>
															<tbody>
																{points[row.login].map((item) => (
																	<tr key={item.pos_id}>
																		<td>
																			{item.name}
																			{item.status !== 1 && (
																				<span className="badge bg-warning ms-1">отключена</span>
																			)}
																		</td>
																		<td>{item.region_name || "—"}</td>
																		<td className="text-end">{amount(item.tariff)}</td>
																		<td className="text-end" style={{ color: item.balance < 0 ? "#d23434" : "inherit" }}>
																			{amount(item.balance)}
																		</td>
																		<td className="text-end">{amount(item.gtv)}</td>
																		<td className="text-end">{formatNumber(item.cheques)}</td>
																		<td className="text-center">{item.last_cheque_at ? formatDate(item.last_cheque_at, "dd.MM.yyyy") : "—"}</td>
																	</tr>
																))}
															</tbody>
														</table>
													)}
												</td>
											</tr>
										)}
									</React.Fragment>
								))}
							</tbody>
						</table>
					</div>
				)}
			</Card>
		</>
	)
}

export default Brands
